import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiRequest from "./AxiosConfiguration";

// Fetch mood list
export const fetchMoods = createAsyncThunk(
  "happinessIndex/fetchMoods",
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiRequest("GET", "HappinessIndex/GetMoods");
      return res.data;
    } catch (err) {
      return rejectWithValue("Failed to fetch moods");
    }
  },
);

// Check if user already submitted today
export const checkToday = createAsyncThunk(
  "happinessIndex/checkToday",
  async (userId, { rejectWithValue }) => {
    try {
      const res = await apiRequest(
        "GET",
        `HappinessIndex/CheckToday?userId=${userId}`,
      );
      return res.data?.result ?? res.data;
    } catch (err) {
      return rejectWithValue("Check failed");
    }
  },
);

// Save happiness index entry
export const saveHappinessIndex = createAsyncThunk(
  "happinessIndex/save",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await apiRequest("POST", "HappinessIndex/Save", payload);
      return res.data;
    } catch (err) {
      return rejectWithValue("Save failed");
    }
  },
);

export const fetchDashboard = createAsyncThunk(
  "happinessIndex/fetchDashboard",
  async ({ employeeId, companyId, date }, { rejectWithValue }) => {
    try {
      const dateStr = date || new Date().toISOString().split("T")[0];
      const res = await apiRequest(
        "GET",
        `Dashboard/GetDashboard?employeeId=${employeeId}&CompanyId=${companyId}&date=${dateStr}`,
      );
      return res.data;
    } catch {
      return rejectWithValue("Failed to load dashboard");
    }
  },
);

export const fetchMatrix = createAsyncThunk(
  "happinessIndex/fetchMatrix",
  async ({ companyId, date }, { rejectWithValue }) => {
    try {
      const dateStr = date || new Date().toISOString().split("T")[0];
      const res = await apiRequest(
        "GET",
        `Dashboard/GetMatrix?CompanyId=${companyId}&date=${dateStr}`,
      );
      return res.data;
    } catch {
      return rejectWithValue("Failed to load matrix");
    }
  },
);

export const fetchEmployeeNames = createAsyncThunk(
  "happinessIndex/fetchEmployeeNames",
  async ({ mood, session, companyId, date }, { rejectWithValue }) => {
    try {
      const dateStr = date || new Date().toISOString().split("T")[0];
      const res = await apiRequest(
        "GET",
        `Dashboard/GetEmployeeNames?mood=${mood}&session=${session}&CompanyId=${companyId}&date=${dateStr}`,
      );
      return { key: `${mood}_${session}`, names: res.data };
    } catch {
      return rejectWithValue("Failed");
    }
  },
);

export const fetchMatrixEmployeeNames = createAsyncThunk(
  "happinessIndex/fetchMatrixEmployeeNames",
  async ({ startMood, endMood, companyId, date }, { rejectWithValue }) => {
    try {
      const dateStr = date || new Date().toISOString().split("T")[0];
      const res = await apiRequest(
        "GET",
        `Dashboard/GetMatrixEmployeeNames?startMood=${startMood}&endMood=${endMood}&CompanyId=${companyId}&date=${dateStr}`,
      );
      return { key: `${startMood}_${endMood}`, names: res.data };
    } catch {
      return rejectWithValue("Failed");
    }
  },
);
// export const fetchMatrix = createAsyncThunk(
//   "happinessIndex/fetchMatrix",
//   async ({ companyId, date, month = 0, year = 0, employeeId = 0, dayView = 1 }, { rejectWithValue }) => {
//     try {
//       const dateStr = date || new Date().toISOString().split("T")[0];
//       const res = await apiRequest("GET",
//         `Dashboard/GetMatrix?CompanyId=${companyId}&date=${dateStr}&month=${month}&year=${year}&employeeId=${employeeId}&dayView=${dayView}`
//       );
//       return res.data;
//     } catch {
//       return rejectWithValue("Failed to load matrix");
//     }
//   }
// );

const happinessIndexSlice = createSlice({
  name: "happinessIndex",
  initialState: {
    moods: [],
    checkResult: null,
    loading: false,
    saveSuccess: false,
    error: null,
    dashboard: null,
    matrix: null,
    employeeNames: {},
  selfMatrix: null,   
  matrixMeta: null, 
  },
  reducers: {
    resetHappinessIndex: (state) => {
      state.checkResult = null;
      state.saveSuccess = false;
      state.error = null;
    },
    setCheckResult: (state, action) => {
      state.checkResult = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMoods.fulfilled, (state, action) => {
        state.moods = action.payload ?? [];
      })
      .addCase(fetchMoods.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(checkToday.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkToday.fulfilled, (state, action) => {
        state.loading = false;
        state.checkResult = action.payload;
      })
      .addCase(checkToday.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(saveHappinessIndex.pending, (state) => {
        state.loading = true;
      })
      .addCase(saveHappinessIndex.fulfilled, (state) => {
        state.loading = false;
        state.saveSuccess = true;
      })
      .addCase(saveHappinessIndex.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchDashboard.fulfilled, (state, action) => {
        state.dashboard = action.payload;
      })
      .addCase(fetchMatrix.fulfilled, (state, action) => {
        state.matrix = action.payload;
      })
      .addCase(fetchEmployeeNames.fulfilled, (state, action) => {
        state.employeeNames[action.payload.key] = action.payload.names;
      })
      .addCase(fetchMatrixEmployeeNames.fulfilled, (state, action) => {
        state.employeeNames[action.payload.key] = action.payload.names;
      });
  },
});

export const { resetHappinessIndex, setCheckResult } =
  happinessIndexSlice.actions;
export default happinessIndexSlice.reducer;
